import { Box, Divider, FormSelect } from "common/components";
import { addFlowStepPrefix } from "common/utils";
import { StepMessages } from "core/actions/flows/ActionDialog/components";
import {
  FlowStepComponentProps,
  StyledStepContentWrapper,
  getStepComponent,
} from "core/actions/flows/steps";
import {
  EntityType,
  IconVariant,
  SelectStepFlowData,
  SelectStepFlowStep,
} from "core/api";
import { WebSetupToolbar } from "core/components";
import { useTranslation } from "i18n";
import { useEffect } from "react";
import { useController } from "react-hook-form";
import { StorageKey } from "../../../../appSettings";

export type SelectStepProps = FlowStepComponentProps<
  SelectStepFlowStep,
  SelectStepFlowData
>;

/** SelectStep step component */
export function SelectStep({
  control,
  flow,
  data,
  flowStep,
  setValue,
  setCurrentSubStepId,
  ...props
}: SelectStepProps): JSX.Element {
  const selectedStepIdKey = addFlowStepPrefix(flowStep, "selectedStepId");

  const { t } = useTranslation(["common"]);
  const {
    steps = [],
    addEmptySelection,
    sortInAlphabeticalOrder,
    selectionMode,
  } = flowStep.config;

  const userLastSelection = JSON.parse(
    localStorage.getItem(StorageKey.LAST_SELECTED_STEP) || "{}"
  );

  const userLastSelectedStepId = userLastSelection[flowStep.stepId] as number;
  const userLastSelectedStep = steps.find(
    (step) => step?.stepId === userLastSelectedStepId
  );

  const { field } = useController({
    control,
    name: selectedStepIdKey,
    defaultValue: userLastSelectedStep?.id || data.selectedStepId,
  });

  const selectedStep = steps.find((step) => field.value === step.id);

  const StepComponent = getStepComponent(selectedStep?.uiComponent);

  useEffect(() => {
    setCurrentSubStepId?.(field.value);
    const updatedSettings = {
      ...userLastSelection,
      [flowStep.stepId]: selectedStep?.stepId,
    };

    localStorage.setItem(
      StorageKey.LAST_SELECTED_STEP,
      JSON.stringify(updatedSettings)
    );
  }, [
    field.value,
    setCurrentSubStepId,
    selectedStep,
    userLastSelection,
    flowStep.stepId,
  ]);

  const dataList = addEmptySelection
    ? [
        { name: t("common:noValueSelected"), id: "-1", description: "" },
        ...steps.map(({ name, id, description, icon }) => ({
          name,
          id,
          description,
          icon: { name: icon, variant: IconVariant.CLOSED },
        })),
      ]
    : steps.map(({ name, id, description, icon }) => ({
        name,
        id,
        description,
        icon: { name: icon, variant: IconVariant.CLOSED },
      }));

  if (sortInAlphabeticalOrder) {
    dataList.sort((a, b) => a.name.localeCompare(b.name));
  }

  return (
    <StyledStepContentWrapper
      dialogSize={flow.config.dialogSize}
      sx={{ padding: 0 }}
    >
      <WebSetupToolbar
        entityType={EntityType.FLOW_STEP_DEFINITION}
        entityId={selectedStep?.stepId}
      />

      <FormSelect
        selectionMode={selectionMode}
        control={control}
        fieldName={selectedStepIdKey}
        validValues={dataList}
      />

      <Divider />
      {selectedStep && (
        <Box sx={{ overflow: "auto" }}>
          <Box>
            <StepMessages messages={flow.data[selectedStep.id]?.messages} />
          </Box>
          <Box>
            <StepComponent
              control={control}
              flow={flow}
              data={flow.data[selectedStep.id]}
              flowStep={selectedStep}
              setValue={setValue}
              {...props}
            />
          </Box>
        </Box>
      )}
    </StyledStepContentWrapper>
  );
}
